import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  listingsApi,
  type CreateListingPayload,
  type ListingDetailDto,
} from "@/api/buyers";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import {
  AgentFieldsBlock,
  agentFormErrors,
  agentFormFrom,
  agentHasIdentity,
  agentToPayload,
  EMPTY_AGENT_FORM,
  type AgentForm,
} from "./ListingAgentDirectory";
import {
  EMPTY_LISTING_FORM,
  ListingDuplicateWarning,
  ListingFieldsBlock,
  listingFormFrom,
  listingNumberErrors,
  listingToPayload,
  useListingDuplicates,
  type ListingForm,
} from "./ListingEditModal";

/**
 * Paste a marketplace listing to create one.
 *
 * <p>Two phases: paste, then an <b>editable</b> confirm form prefilled from the
 * extraction. Nothing auto-creates — the AI proposes and the specialist is the
 * one who saves, which is why every extracted value lands in an ordinary input
 * rather than a read-only summary.</p>
 *
 * <p>🔴 <b>The agent is passed through as its own block, never flattened.</b> A
 * listing agent is a SHARED record deduped by email, and the create endpoint
 * get-or-creates it: flattening the extracted agent onto the listing would drop
 * the record entirely, and there is no other field on the listing that could
 * carry it. So the extraction's <code>agent</code> becomes the payload's
 * <code>newAgent</code>, and the specialist can correct it first.</p>
 */
export function PasteToCreateListingModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (created: ListingDetailDto) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  // Phase 1 — paste.
  const [text, setText] = useState("");
  const [extractError, setExtractError] = useState<string | null>(null);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  // Phase 2 — review.
  const [reviewing, setReviewing] = useState(false);
  const [form, setForm] = useState<ListingForm>(EMPTY_LISTING_FORM);
  const [agent, setAgent] = useState<AgentForm>(EMPTY_AGENT_FORM);
  const [hasAgent, setHasAgent] = useState(false);
  const [modelUsed, setModelUsed] = useState<string | null>(null);

  const reset = () => {
    setText("");
    setExtractError(null);
    setFallbackNote(null);
    setReviewing(false);
    setForm(EMPTY_LISTING_FORM);
    setAgent(EMPTY_AGENT_FORM);
    setHasAgent(false);
    setModelUsed(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const numberErrors = listingNumberErrors(form);
  const agentErrors = agentFormErrors(agent);
  const canSave =
    Object.keys(numberErrors).length === 0 &&
    (!hasAgent || (agentHasIdentity(agent) && !agentErrors.email && !agentErrors.phone));

  const duplicates = useListingDuplicates({
    rawUrl: form.rawUrl,
    businessName: form.businessName,
    enabled: open && reviewing,
  });
  const matches = duplicates.data?.matches ?? [];

  const extract = useMutation({
    mutationFn: () => listingsApi.pasteExtract(text.trim()),
    onSuccess: (result) => {
      setForm({ ...listingFormFrom(result), notes: result.notesSummary ?? "" });
      // The extraction's own block, kept whole.
      setAgent(agentFormFrom(result.agent));
      setHasAgent(result.agent != null);
      setModelUsed(result.modelUsed ?? null);
      setExtractError(null);
      setFallbackNote(null);
      setReviewing(true);
    },
    onError: (err: unknown) => {
      // A 422 means "we couldn't read this one" — drop into an empty form to
      // fill in by hand rather than losing the paste. Render the SERVER's
      // message: a 422 is raised both when the AI could not read the text and
      // when the daily AI cost ceiling is reached, and those read completely
      // differently to the person waiting on it.
      if (err instanceof ApiError && err.status === 422) {
        setForm(EMPTY_LISTING_FORM);
        setAgent(EMPTY_AGENT_FORM);
        setHasAgent(false);
        setModelUsed(null);
        setFallbackNote(
          err.message.trim() ||
            "We couldn't read that automatically — add the details below; your pasted text is kept.",
        );
        setReviewing(true);
      } else {
        setExtractError(extractErrorMessage(err));
      }
    },
    // no-invalidation: reads a pasted blob into a prefill and persists nothing.
  });

  const save = useMutation({
    mutationFn: () => {
      const body: CreateListingPayload = listingToPayload(form, { forEdit: false });
      if (hasAgent && agentHasIdentity(agent)) {
        body.newAgent = agentToPayload(agent, { forEdit: false });
      }
      if (matches.length > 0) body.confirmCreateDespiteDuplicate = true;
      return listingsApi.create(body);
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      toast.success("Listing created.");
      onCreated?.(created);
      close();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      open={open}
      onClose={close}
      title={reviewing ? "Review & save listing" : "Paste to create a listing"}
      size="lg"
      footer={
        <>
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          {reviewing ? (
            <Button disabled={save.isPending || !canSave} onClick={() => save.mutate()}>
              {save.isPending
                ? "Saving…"
                : matches.length > 0
                  ? "Create anyway"
                  : "Create listing"}
            </Button>
          ) : (
            <Button
              disabled={extract.isPending || text.trim().length === 0}
              onClick={() => extract.mutate()}
            >
              <Sparkles size={14} strokeWidth={1.5} aria-hidden />
              {extract.isPending ? "Extracting…" : "Extract"}
            </Button>
          )}
        </>
      }
    >
      {!reviewing ? (
        <div className="grid gap-space-3">
          <p className="text-small text-gray-mid m-0">
            Paste a business-for-sale listing — the page text, or the broker&apos;s
            email. We&apos;ll pull out the details and the listing agent for you to
            check before anything is saved.
          </p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder="Paste here…"
            aria-label="Paste a listing to extract"
          />
          {extractError && (
            <p className="text-caption text-status-error m-0" role="alert">
              {extractError}
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-space-4">
          {fallbackNote && (
            <div
              className="rounded-md border border-status-info bg-white-warm px-space-3 py-space-2 text-caption text-onyx"
              role="status"
            >
              {fallbackNote}
            </div>
          )}

          {matches.length > 0 && (
            <ListingDuplicateWarning
              matches={matches}
              normalizedUrl={duplicates.data?.normalizedUrl}
            />
          )}

          <ListingFieldsBlock value={form} onChange={setForm} errors={numberErrors} />

          <div className="grid gap-space-3 border-t border-border-light pt-space-4">
            <div className="flex items-center justify-between gap-space-3">
              <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
                Listing agent
              </h3>
              {!hasAgent && (
                <Button variant="text" size="sm" onClick={() => setHasAgent(true)}>
                  Add an agent
                </Button>
              )}
            </div>
            {hasAgent ? (
              <>
                <p className="text-caption text-gray-mid m-0">
                  Matched by email address — if this agent is already on file
                  we&apos;ll attach to that record rather than creating a second one.
                </p>
                <AgentFieldsBlock value={agent} onChange={setAgent} errors={agentErrors} />
                <div>
                  <Button variant="text" size="sm" onClick={() => setHasAgent(false)}>
                    Save without an agent
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-caption text-gray-mid m-0">
                No agent found in the pasted text. A listing without one can still
                be worked — it just has no named contact on the seller&apos;s side.
              </p>
            )}
          </div>

          {/* The model identifier is deliberately not published to staff — the
              same call already made on the buyer page and in the wizard. Which
              model ran is an implementation detail that changes without notice;
              what the reader needs to know is that a human did not type this. */}
          {modelUsed && (
            <p className="text-caption text-gray-mid m-0">
              Extracted automatically from pasted text.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
